import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CheckOutlined } from "@ant-design/icons";
import { MdOutlineChevronRight } from "react-icons/md";
import { Formik, Field } from "formik";

import { ConfirmationModal } from "../../../../../components/ConfirmationModal";
import LoadingElevaty from "../../../../../components/LoadingElevaty";
import { usePermissions } from "../../../../../Context/permissionContext";
import { useAuth } from "../../../../../hooks/AuthProvider";
import api from "../../../../../services/api";
import { notifyError, notifySucces } from "../../../../../utils/notifications";
import ModalDetailInsurance from "../../../../home/modals/modalDetailInsurance";
import { ModalSendMailOrSms } from "../../../../home/modals/ModalSendMailOrSms/ModalSendMailOrSms";
import * as S from "./style";
import DSButton from "../../../../../components/DSButton";
import { ActiveInsurances } from "./ActiveInsurances";
import { CanceledInsurances } from "./CanceledInsurances";
import { TextArea } from "../../../../../components/TextArea";
import { cancelReasonSchema } from "./schema";
import { InsuranceAgreementModal } from "../../../../../components/InsuranceAgreementModal";
import { Tabs } from "../../../../../components-v2/Tabs";
import {
  formatClientAndDependents,
  formatClientTitular,
} from "../../../../../components-v2/ClientSelectionItem/format-client-and-dependents";
import { ClientSelectionItem } from "../../../../../components-v2/ClientSelectionItem";
import {
  INSURANCE_ADHESION_PUSH_LABEL,
  INSURANCE_ADHESION_SAC_MODAL_LABEL,
  resolveInsuranceAdhesionActionVisibility,
} from "../../../../../helpers/getInsuranceAdhesionActionVisibility";
import { useSendInsuranceAdhesionPush } from "../../../../../hooks/useSendInsuranceAdhesionPush";
import {
  INSURANCE_CANCEL_PUSH_CONFIRM_DESCRIPTION,
  INSURANCE_CANCEL_PUSH_CONFIRM_TITLE,
} from "../../../../../helpers/getInsuranceCancelActionVisibility";
import { useSendInsuranceCancelPush } from "../../../../../hooks/useSendInsuranceCancelPush";

const ModalViewInsurances = ({
  client,
  open,
  closeModal,
  setDependents,
  clientId,
  clientInsurances,
  openNewInsuranceModal,
  reloadClientInsurances,
  canceledInsurances,
  reloadCanceledInsurances,
}) => {
  const { user } = useAuth();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [insurances, setInsurances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMailModal, setLoadingMailModal] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [listDependents, setListDependents] = useState([]);
  const [handleClickRow, setHandleClickRow] = useState({});
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [
    selectedInsuranceForCancellation,
    setSelectedInsuranceForCancellation,
  ] = useState(null);
  const [isSendMailModalOpen, setIsSendMailModalOpen] = useState(false);
  const [holderOrDependent, setIsHolderOrDependent] = useState("titular");
  const [
    selectedInsuranceForAgreementModal,
    setSelectedInsuranceForAgreementModal,
  ] = useState(null);
  const [isCancelInsuranceLoading, setIsCancelInsuranceLoading] =
    useState(false);
  const [isCancelViaAppConfirmOpen, setIsCancelViaAppConfirmOpen] =
    useState(false);
  const [allowCancelViaApp, setAllowCancelViaApp] = useState(false);

  const { permissions } = usePermissions();
  const { sendInsuranceAdhesionPush, sendingInsurancePush } =
    useSendInsuranceAdhesionPush();
  const { sendInsuranceCancelPush, sendingInsuranceCancelPush } =
    useSendInsuranceCancelPush();
  const { showSacInsuranceAction, showPushInsuranceAction } = useMemo(
    () => resolveInsuranceAdhesionActionVisibility(permissions),
    [permissions]
  );

  const formikRef = useRef(null);

  const clientsWithDependents = formatClientAndDependents({
    clientCtx: client,
    dependents: listDependents,
  });

  const handleClickSendEmail = (insurance) => {
    setHandleClickRow(insurance);
    setIsSendMailModalOpen(true);
  };

  const printTicketInsurance = useCallback(
    async (row) => {
      try {
        const resp = await api.get(
          "insurance_ticket_contracts",
          { client_id: clientId, contract_id: row.codigoAdesao },
          { "Content-Type": "application/pdf" },
          true
        );
        const fileURL = URL.createObjectURL(await resp.blob());
        const pdfWindow = window.open(fileURL);
        setTimeout(() => {
          pdfWindow.document.title = "Bilhete_Contrato.pdf";
        }, 750);
      } catch (error) {
        notifyError("Seguro não possui bilhete para emitir");
      }
    },
    [clientId]
  );

  async function cancelInsurance(row) {
    setIsCancelInsuranceLoading(true);
    await api
      .delete("client_insurances", {
        client_id: clientId,
        codigoAdesao: row?.codigoAdesao,
        meioCancelamento: "SNXPAYMENT",
        usuarioCancelamento: user.username,
        lojaCancelamento: 1,
        descricaoSeguro: row.descricaoSeguro,
        motivoCancelamento:
          formikRef.current?.values.motivoCancelamento || undefined,
        codigoSeguro: row?.codigoSeguro,
      })
      .then(() => {
        notifySucces(`Seguro ${row.descricaoSeguro} cancelado com sucesso`);
        setInsurances(
          insurances.filter((i) => i.codigoAdesao !== row.codigoAdesao)
        );
        reloadClientInsurances();
      })
      .catch((error) => {
        const errorMessage = error.message.split(",")[0];
        notifyError(errorMessage);
      })
      .finally(() => {
        setIsCancelInsuranceLoading(false);
        setIsCancelModalVisible(false);
      });
  }

  const cancelInsuranceDependent = async (row) => {
    setIsCancelInsuranceLoading(true);
    await api
      .delete("dependant_insurances", {
        id:
          selectedBeneficiary?.id === client?.client?.id
            ? undefined
            : selectedBeneficiary?.id,
        client_id: clientId,
        codigoAdesaoDependente: row.codigoAdesaoDependente,
        meioCancelamento: "SNXPAYMENT",
        usuarioCancelamento: user?.username,
        lojaCancelamento: 1,
        descricaoSeguro: row.descricaoSeguro,
        motivoCancelamento:
          formikRef.current?.values.motivoCancelamento || undefined,
        codigoSeguro: row?.codigoSeguro,
      })
      .then(() => {
        notifySucces(`Seguro ${row.descricaoSeguro} cancelado com sucesso`);

        setInsurances(
          insurances.filter((i) => i.codigoAdesao !== row.codigoAdesao)
        );
        reloadClientInsurances();
      })
      .catch((error) => {
        notifyError(`Erro ${error.message}`);
      })
      .finally(() => {
        setIsCancelInsuranceLoading(false);
        setIsCancelModalVisible(false);
      });
  };

  const requestCancelViaApp = useCallback(() => {
    setIsCancelViaAppConfirmOpen(true);
  }, []);

  const handleConfirmCancelViaApp = async () => {
    const sent = await sendInsuranceCancelPush();
    if (sent) setIsCancelViaAppConfirmOpen(false);
  };

  const openActiveInsuranceDetail = (insuranceDetails) => {
    setSelectedInsurance(insuranceDetails);
    setAllowCancelViaApp(true);
    setIsDetailModalOpen(true);
  };

  const openCanceledInsuranceDetail = (insuranceDetails) => {
    setSelectedInsurance(insuranceDetails);
    setAllowCancelViaApp(false);
    setIsDetailModalOpen(true);
  };

  const handleOpenInsuranceAgreementModal = (insurance) => {
    setSelectedInsuranceForAgreementModal(insurance.codigoAdesao);
  };

  const handleCloseInsuranceAgreementModal = () => {
    setSelectedInsuranceForAgreementModal(null);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadDependents = async () => {
      if (!open) {
        return;
      }
      setLoading(true);
      try {
        const dependentData = await setDependents(clientId);
        if (isMounted) {
          setListDependents(dependentData || []);
        }
      } catch (error) {
        if (isMounted) {
          console.error(error);
          setListDependents([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    if (clientId && open) {
      loadDependents();
    }
    return () => {
      isMounted = false;
    };
  }, [clientId, setDependents, open]);

  const handleSelection = async (person, typePerson) => {
    setIsHolderOrDependent(typePerson);
    setSelectedBeneficiary(person);
    setLoading(true);

    if (typePerson === "titular") {
      setInsurances(clientInsurances);
      setLoading(false);
    }

    if (typePerson === "dependente") {
      try {
        const insurancePromises = clientInsurances.map(async (insurance) => {
          const { data } = await api.get("insurance_detail", {
            client_id: clientId,
            codigo_adesao: insurance.codigoAdesao,
          });
          const dependentInsurance = data.dependentes.find(
            (d) => String(d.codigoDependente) === String(person.id)
          );
          return dependentInsurance
            ? { ...insurance, ...dependentInsurance }
            : null;
        });
        const resolvedInsurances = (
          await Promise.all(insurancePromises)
        ).filter(Boolean);
        setInsurances(resolvedInsurances);
      } catch (error) {
        console.error(error);
        setInsurances([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setSelectedBeneficiary(null);
    setListDependents([]);
    setLoading(false);
    setIsCancelViaAppConfirmOpen(false);
    setAllowCancelViaApp(false);
    closeModal();
  };

  const showCancelConfirmationModal = (insurance) => {
    setSelectedInsuranceForCancellation(insurance);
    setIsCancelModalVisible(true);
  };

  const onContractNewInsurance = () => {
    openNewInsuranceModal();
  };

  const handleSendInsuranceMail = async (mail) => {
    try {
      setLoadingMailModal(true);
      await api.post("send_insurance_email", {
        codigo_adesao: handleClickRow.codigoAdesao,
        client_id: client.client.id,
        client_name: client.client.name,
        ticket: client.client.ticket.protocol,
        email: mail,
        product_code: client.product.codigoProduto,
        product_description: client.product.desc_prod,
      });
      setIsSendMailModalOpen(false);
      notifySucces("Enviado com sucesso");
    } catch (error) {
      notifyError("Falha ao enviar email");
    } finally {
      setLoadingMailModal(false);
    }
  };

  useEffect(() => {
    if (open) {
      const formattedClient = formatClientTitular({ clientCtx: client });
      handleSelection(formattedClient, "titular");
    }
  }, [open, client]);

  return (
    <S.ModalViewInsurance
      visible={open}
      title={<S.ModalTitle>Seguros Contratados</S.ModalTitle>}
      width={1013}
      onCancel={handleCancel}
      footer={null}
    >
      {loading && (
        <S.NewLoadingModals>
          <LoadingElevaty width="40px" height="40px" duration="durFast" />
        </S.NewLoadingModals>
      )}
      <>
        <S.NewSelectInsurance>
          <S.SelectedPerson>
            <h2>Para:</h2>
            <S.NewSelectedPerson>
              {clientsWithDependents.map((person) => (
                <ClientSelectionItem
                  key={`modal-view-insurance-client-${person.id}-${person.type}`}
                  client={person}
                  isSelected={selectedBeneficiary?.id === person.id}
                  onSelect={() => handleSelection(person, person.type)}
                />
              ))}
            </S.NewSelectedPerson>
          </S.SelectedPerson>
          <S.VerticalDivider />
          <S.InsuranceInfo>
            <S.NewInsuranceContainer>
              <Tabs
                tabs={[
                  {
                    id: "tab-seguros-ativos",
                    title: "Ativos",
                    content: permissions.includes("view_contracted_insurances") ? (
                      <ActiveInsurances
                        insurances={insurances}
                        printTicketInsurance={printTicketInsurance}
                        handleModalAdhesionAgreement={
                          handleOpenInsuranceAgreementModal
                        }
                        showCancelConfirmationModal={
                          showCancelConfirmationModal
                        }
                        handleClickSendEmail={handleClickSendEmail}
                        openDetailModal={openActiveInsuranceDetail}
                        onRequestCancelViaApp={requestCancelViaApp}
                      />
                    ): (
                      <div style={{ padding: "2rem", textAlign: "center", color: "#8c8c8c" }}>
                        Você não possui permissão para visualizar os seguros ativos.
                      </div>
                    ),
                    disabled: !permissions.includes("view_contracted_insurances"),
                  },
                  {
                    id: "tab-seguros-cancelados",
                    title: "Cancelados",
                    content: permissions.includes("list_canceled_insurance") ? (
                      <CanceledInsurances
                        insurances={canceledInsurances}
                        handleModalAdhesionAgreement={
                          handleOpenInsuranceAgreementModal
                        }
                        handleClickSendEmail={handleClickSendEmail}
                        openDetailModal={openCanceledInsuranceDetail}
                        printTicketInsurance={printTicketInsurance}
                      />
                    ) : (
                      <div style={{ padding: "2rem", textAlign: "center", color: "#8c8c8c" }}>
                        Você não possui permissão para visualizar os seguros cancelados.
                      </div>
                    ),
                    disabled: !permissions.includes("list_canceled_insurance"),
                  },
                ].filter(Boolean)}
                maxWidth
              />
            </S.NewInsuranceContainer>
            <S.ActionButtonsContainer>
              {showPushInsuranceAction && (
                <DSButton
                  htmlType="button"
                  onClick={sendInsuranceAdhesionPush}
                  buttonText={INSURANCE_ADHESION_PUSH_LABEL}
                  icon={<MdOutlineChevronRight />}
                  isIconAfter
                  isLoading={sendingInsurancePush}
                  isDisabled={sendingInsurancePush}
                  disabled={sendingInsurancePush}
                  buttonType="secondary"
                  dataTestId="botao_link_contratacao_seguro_app_modal"
                />
              )}
              {showSacInsuranceAction && (
                <DSButton
                  htmlType="button"
                  onClick={onContractNewInsurance}
                  buttonText={
                    isMobile ? "Contratar +" : INSURANCE_ADHESION_SAC_MODAL_LABEL
                  }
                  icon={<CheckOutlined />}
                  buttonType="primary"
                  dataTestId="botao_contratar_novo_seguro_modal"
                />
              )}
            </S.ActionButtonsContainer>
          </S.InsuranceInfo>
        </S.NewSelectInsurance>
        <ModalDetailInsurance
          open={isDetailModalOpen}
          closeModal={() => {
            setIsDetailModalOpen(false);
            setAllowCancelViaApp(false);
          }}
          insurance={selectedInsurance}
          allowCancelViaApp={allowCancelViaApp}
          onRequestCancelViaApp={requestCancelViaApp}
        />
        {isCancelViaAppConfirmOpen && (
          <ConfirmationModal
            title={INSURANCE_CANCEL_PUSH_CONFIRM_TITLE}
            description={INSURANCE_CANCEL_PUSH_CONFIRM_DESCRIPTION}
            isLoading={sendingInsuranceCancelPush}
            onClose={() => setIsCancelViaAppConfirmOpen(false)}
            onSubmit={handleConfirmCancelViaApp}
            cancelDataTestId="btn_fechar_confirmacao_cancelamento_app"
            submitDataTestId="btn_confirmar_cancelamento_app"
            closeDataTestId="btn_x_confirmacao_cancelamento_app"
          />
        )}
        <S.CancelaInsuranceModal
          title="Confirmar Cancelamento"
          visible={isCancelModalVisible}
          closable={!isCancelInsuranceLoading}
          maskClosable={!isCancelInsuranceLoading}
          keyboard={!isCancelInsuranceLoading}
          onCancel={() => setIsCancelModalVisible(false)}
          footer={null}
        >
          <p>
            Você confirma o cancelamento do seguro{" "}
            <strong>
              {" "}
              {selectedInsuranceForCancellation?.descricaoSeguro}
            </strong>
            ?
          </p>
          <Formik
            innerRef={formikRef}
            initialValues={{ motivoCancelamento: "" }}
            validationSchema={cancelReasonSchema}
            validateOnMount
            validateOnChange
          >
            {({ isValid, isValidating }) => (
              <>
                <Field name="motivoCancelamento">
                  {({ field, meta }) => (
                    <TextArea
                      label="Motivo do Cancelamento"
                      value={field.value}
                      onChange={field.onChange}
                      error={meta.touched && meta.error}
                      maxLength={1024}
                      name={field.name}
                      onBlur={field.onBlur}
                    />
                  )}
                </Field>

                <S.ActionCancelButtons>
                  <DSButton
                    style={{
                      marginRight: "8px",
                      height: "30px",
                      padding: "0rem 1rem",
                    }}
                    isCancelType
                    buttonText="Cancelar"
                    buttonType="secondary"
                    isDisabled={isCancelInsuranceLoading}
                    onClick={() => setIsCancelModalVisible(false)}
                  />

                  <DSButton
                    type="primary"
                    buttonText="Confirmar"
                    buttonType="secondary"
                    style={{
                      height: "30px",
                      padding: "0rem 1rem",
                    }}
                    disabled={isValidating || !isValid}
                    isLoading={isCancelInsuranceLoading}
                    onClick={() => {
                      if (holderOrDependent === "titular") {
                        cancelInsurance(selectedInsuranceForCancellation);
                      } else if (holderOrDependent === "dependente") {
                        cancelInsuranceDependent(
                          selectedInsuranceForCancellation
                        );
                      }
                    }}
                  />
                </S.ActionCancelButtons>
              </>
            )}
          </Formik>
        </S.CancelaInsuranceModal>
      </>
      {isSendMailModalOpen && (
        <ModalSendMailOrSms
          isOpen={isSendMailModalOpen}
          isSubmitting={loadingMailModal}
          onClose={() => setIsSendMailModalOpen(false)}
          isSmsEnabled={false}
          clientMail={client?.client?.email}
          onSubmitMail={handleSendInsuranceMail}
        />
      )}
      <>
        <InsuranceAgreementModal
          isOpen={!!selectedInsuranceForAgreementModal}
          onClose={handleCloseInsuranceAgreementModal}
          clientId={clientId}
          contractId={selectedInsuranceForAgreementModal}
        />
      </>
    </S.ModalViewInsurance>
  );
};

export default ModalViewInsurances;
